import type { QuestionType } from './types';

/** Allowed per-question time limits, in seconds. */
export const TIME_LIMITS = [5, 10, 20, 30, 45, 60, 90, 120, 240] as const;
export const DEFAULT_TIME_LIMIT = 20;

export const LIMITS = {
  quizTitle: 100,
  quizDescription: 500,
  questionText: 240,
  choiceText: 90,
  minChoices: 2,
  maxChoices: 4,
  maxQuestions: 1000,
  nickname: 20,
  imageUrl: 2048,
  /** Max uploaded image size in bytes (after client-side resize). */
  imageBytes: 2.5 * 1024 * 1024,
} as const;

/** Base points per question, by points mode. */
export const POINTS_BASE = { standard: 1000, double: 2000, none: 0 } as const;

/** Streak bonus: +100 per consecutive correct answer after the first, capped. */
export const STREAK_BONUS_STEP = 100;
export const STREAK_BONUS_MAX = 500;

/** How long the "get ready" intro is shown before answers open (ms). */
export const INTRO_MS = 5000;
/** Answers arriving up to this long after the deadline are still accepted (network latency). */
export const ANSWER_GRACE_MS = 500;
/** Delay between "everyone answered" and the automatic reveal (ms). */
export const ALL_ANSWERED_REVEAL_DELAY_MS = 600;
/** How many entries the between-question scoreboard shows. */
export const SCOREBOARD_SIZE = 5;

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  quiz: 'Quiz',
  multi: 'Multi-select',
  truefalse: 'True / False',
  poll: 'Poll',
};

export const QUESTION_TYPE_HINTS: Record<QuestionType, string> = {
  quiz: 'Pick one answer',
  multi: 'Select all correct answers',
  truefalse: 'True or false?',
  poll: 'No right or wrong, just vote',
};

export type ShapeName = 'triangle' | 'diamond' | 'circle' | 'square';
export type ChoiceColor = 'red' | 'blue' | 'yellow' | 'green';

export interface ChoiceAppearance {
  shape: ShapeName;
  color: ChoiceColor;
  /** Short accessible label, e.g. "Red triangle". */
  label: string;
}

const APPEARANCES: ChoiceAppearance[] = [
  { shape: 'triangle', color: 'red', label: 'Red triangle' },
  { shape: 'diamond', color: 'blue', label: 'Blue diamond' },
  { shape: 'circle', color: 'yellow', label: 'Yellow circle' },
  { shape: 'square', color: 'green', label: 'Green square' },
];

/**
 * Shape + colour for the choice shown at `displayIndex`.
 * True/False uses blue diamond for "True" (index 0) and red triangle for "False" (index 1).
 */
export function getChoiceAppearance(type: QuestionType, displayIndex: number): ChoiceAppearance {
  if (type === 'truefalse') return displayIndex === 0 ? APPEARANCES[1]! : APPEARANCES[0]!;
  return APPEARANCES[displayIndex % APPEARANCES.length]!;
}

/** Hex values for the four answer colours (Google palette, tuned for contrast). */
export const CHOICE_COLORS: Record<ChoiceColor, { bg: string; text: string }> = {
  red: { bg: '#E8453C', text: '#FFFFFF' },
  blue: { bg: '#1A73E8', text: '#FFFFFF' },
  yellow: { bg: '#F9AB00', text: '#202124' },
  green: { bg: '#1E8E3E', text: '#FFFFFF' },
};

/**
 * Saved game results keep per-answer detail only up to this many answers (players x
 * questions); larger games store ranks, scores and correct counts (~100 MB otherwise).
 */
export const RESULT_DETAIL_MAX_ANSWERS = 250_000;

export const QUIZ_FILE_FORMAT = 'quiz-dayro-quiz';
/** Files exported before the rename to Quiz Dayro still import. */
export const LEGACY_QUIZ_FILE_FORMATS = ['sasta-kahoot-quiz'] as const;
export const QUIZ_FILE_VERSION = 1;
